import { ArrowRightIcon } from 'lucide-react'
import { useMemo } from 'react'

import { PRICE_QUALIFIER_TEXT } from 'components/marketplace/shared'
import { type MarketplacePriceCadence } from 'components/marketplace/shared/types'
import { getPriceCadenceSuffix } from 'components/marketplace/shared/utils'
import { AnimatedCurrency } from 'components/member_onboarding_v2/components/AnimatedCurrency'
import { Button, TextLink } from 'slab'
import { type GrapeMoney } from 'types'
import {
  useGetMarketplaceProducts
} from 'utils/web_api'
import { type MarketplaceProduct } from 'utils/web_api.schemas'

import { getMonthlySubunits, getNumberOfCoverageMonths, pickMarketplaceProductsForBudget } from '../../../smartBudgetUtils'
import { type MarketplaceProductForBudget } from '../../../types'

const MarketplaceProductCard = ({ product }: { product: MarketplaceProduct }) => {
  const priceCadenceSuffix = product.price_cadence ? getPriceCadenceSuffix(product.price_cadence as MarketplacePriceCadence) : ''

  return (
    <div className='w-full h-full bg-gray-50 rounded-lg border border-gray-200 p-3 flex flex-row gap-3'>
      {product.image_url && (
        <div className='w-[85px] h-16 shrink-0 bg-white rounded-lg border border-gray-200'>
          <img
            src={product.image_url}
            alt={`Picture of ${product.name}`}
            className='w-full h-full rounded-lg object-cover'
          />
        </div>)}
      <div className='flex flex-col gap-[2px]'>
        <div className='body-sm text-gray-500'>{product.product_brand}</div>
        <div className='body-bold text-main'>{product.name}</div>
        <div className='pt-2'>
          {product.has_price_qualifier && (
            <span className='body-sm text-main'>{PRICE_QUALIFIER_TEXT} </span>
          )}
          <span className='body-sm-bold text-green-400 pt-2'>{product.base_price.formatted_amount}</span>
          {priceCadenceSuffix && (
            <span className='body-sm text-main'>{priceCadenceSuffix}</span>
          )}
        </div>
      </div>
    </div>
  )
}

interface SmartBudgetAllowanceCardProps {
  monthlyAllowance: GrapeMoney
  totalMonthlyPremiums: GrapeMoney
  qmeEnabled: boolean
  soonestCoverageStartDate?: string
  onContinueClick?: () => void
}

export const SmartBudgetAllowanceCard = ({
  monthlyAllowance,
  totalMonthlyPremiums,
  qmeEnabled,
  soonestCoverageStartDate,
  onContinueClick,
}: SmartBudgetAllowanceCardProps) => {
  // We don't care about the error state, we will just gracefully show the card without products
  const { data: marketplaceProducts } = useGetMarketplaceProducts(undefined, {
    query: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  })

  const numberOfCoverageMonths = useMemo(() => {
    return getNumberOfCoverageMonths(soonestCoverageStartDate)
  }, [soonestCoverageStartDate])

  const sortedProducts: MarketplaceProductForBudget[] = useMemo(() => {
    if (!marketplaceProducts || !marketplaceProducts.data) return []
    return marketplaceProducts.data.map((product) => ({
      ...product,
      monthlySubunits: getMonthlySubunits(product, numberOfCoverageMonths),
    })).sort((a, b) => a.monthlySubunits - b.monthlySubunits)
  }, [marketplaceProducts, numberOfCoverageMonths])

  const remainingBalance = monthlyAllowance.subunits - totalMonthlyPremiums.subunits

  const { header, balanceColor, currencyValue } = useMemo(() => {
    return {
      header: remainingBalance >= 0 ? 'Remaining balance' : 'You\'ll pay',
      balanceColor: remainingBalance >= 0 ? 'text-green-400' : 'text-red-500',
      // We don't want to show the negative sign, so we use the absolute value
      currencyValue: Math.abs(remainingBalance / 100),
    }
  }, [remainingBalance])

  const products = useMemo(() => {
    return pickMarketplaceProductsForBudget(sortedProducts, remainingBalance)
  }, [sortedProducts, remainingBalance])

  return (
    <div className='w-full border-emphasis shadow-emphasis rounded-2xl'>
      {/* Top half (Remaining balance) */}
      <div className='p-6'>
        <span className='body-sm text-gray-500'>{header}</span>
        <div className='mb-4'>
          <AnimatedCurrency
            aria-hidden
            className={`${balanceColor} heading-xl`}
            value={currencyValue}
          />
          {/* Note the space before /mo */}
          <span className='heading-sm-regular'> /mo</span>
        </div>
        {/* Monthly allowance, Insurance premiums */}
        <div className='flex flex-col gap-2'>
          <div className='flex justify-between'>
            <span className='body-sm text-gray-500'>Monthly allowance</span>
            <span className='body-sm-bold'>{monthlyAllowance.formatted_amount}</span>
          </div>
          <div className='flex justify-between'>
            <span className='body-sm text-gray-500'>Insurance premiums</span>
            <span className='body-sm-bold'>-{totalMonthlyPremiums.formatted_amount}</span>
          </div>
        </div>
        {/* Continue button */}
        <div className='mt-6'>
          <Button variant='primary' width='full' rightIcon={ArrowRightIcon} size='large' onClick={onContinueClick}>Continue</Button>
        </div>
      </div>
      {/* Bottom half (Marketplace) */}
      {qmeEnabled && remainingBalance > 0 && (
        <div className='p-6 bg-gray-50 border-t border-gray-200 gap-4 flex flex-col'>
          <span className='body-bold text-main'>Use your balance on other healthcare costs</span>
          <span className='body-sm text-gray-500 pb-1'>Your Thatch balance accumulates every month, and can be spent on healthcare expenses like copays, prescriptions, and items in the <TextLink href='/public/marketplace' target='_blank'>Thatch Marketplace</TextLink> like:</span>
          {products.map((product) => (
            <MarketplaceProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  )
}
